import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import type { TData } from "@src/helpers/zustand";
import useZustandStore from "@src/helpers/zustand";
import { saveCustomerApiCall } from "@src/api/useCustomer";
import { LoggerTypes, useKYCAddUserLog } from "@src/api/useKYCAddUserLog";

export const useSaveUser = (onSuccessCallback: () => void) => {
  const { i18n } = useTranslation();
  const { data: zData, setData } = useZustandStore((state) => state);
  const navigate = useNavigate();
  const addUserLogMutation = useKYCAddUserLog();

  const saveCustomer = useMutation(saveCustomerApiCall, {
    onSuccess() {
      onSuccessCallback();
      addUserLogMutation.mutate({
        eventName: LoggerTypes.OnboardingCompletedSuccessFully,
      });
    },
    onError({ response }) {
      setData({
        ...zData,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- we trust the api
        errorMessage: response.data.displayText || response.data.DisplayText,
      });
      navigate(`/${i18n.language}/error`);
    },
  });

  const onSaveUser = (data?: TData) => {
    const rightData = data || zData;

    saveCustomer.mutate({
      apiKey: rightData.apiKey,
      expectedTransactionTypes: rightData.expectedTransactionTypes,
      birthDate: rightData.closeSession.birthDate,
      issueDate: rightData.closeSession.issueDate,
      validTo: rightData.closeSession.expirationDate,
      documentBackSide: rightData.closeSession.documentBackSide,
      documentFrontSide: rightData.closeSession.documentFrontSide,
      selfImage: rightData.closeSession.selfImage,
      documentType: rightData.closeSession.documetType,
      name: rightData.closeSession.firstName.trim(),
      personalID: rightData.closeSession.personalNumber,
      surname: rightData.closeSession.lastName.trim(),
      employmentStatusCode: rightData.employment.type,
      employer: rightData.employment.employer,
      workPosition: rightData.employment.workPosition.trim(),
      // business
      employmentStatusBusinesOwnerType: rightData.employment.bussinessType,
      employmentEntrepreneurWorkType: rightData.employment.entrepreneurDetails, // list of work types
      employmentEntrepreneurWorkTypeOther:
        rightData.employment.entrepreneurDetailsOther,
      employmentLegalWorkType: rightData.employment.legalWorkType, // legal name
      employmentLegalCustomerINN:
        rightData.employment.legalIdentificationNumber,
      employmentLegalName: rightData.employment.legalName, // legal name
      employmentLegalWorkTypeOther: rightData.employment.legalWorkTypeOther,

      expectedTurnoverCode: rightData.expectedTurnoverCode,
      citizenshipCountryID: Number(rightData.citizenship.passportCountry),
      passportNumber: rightData.closeSession.documentNumber,
      factCountryID:
        rightData.address.factCountryID || rightData.address.legalCountry,
      factCity:
        rightData.address.factCity.trim() || rightData.address.legalCity.trim(),
      factCityID: 0,
      factAddress:
        rightData.address.factAddress.trim() ||
        rightData.address.legalAddress.trim(),
      secondaryCitizenshipCountryID: rightData.citizenship.otherCountry,
      legalCountryID: rightData.address.legalCountry,
      legalCityID: 0,
      legalCity: rightData.address.legalCity.trim(),
      legalAddress: rightData.address.legalAddress.trim(),
      sex: rightData.closeSession.sex === "male" ? 1 : 0,
      birthCity: rightData.citizenship.birthCity.trim(),
      birthCityId: 0,
      phone: `${rightData.dialCode}${rightData.phone}`,
      worktypes: rightData.employment.worktypes,
      authority: rightData.closeSession.authority.trim(),
      birthCountryId: rightData.citizenship.birthCountry,
      sourceOfIncomeCodes: rightData.sourceOfIncomeCodes,
      sourceOfIncomeOther: rightData.sourceOfIncomeOther,
      monthlyIncomeCode: rightData.monthlyIncomeCode,
      pepType: rightData.pep.type,
      pepPosition: rightData.pep.pepWorkingPosition,
      pepConnectionType: rightData.pep.pepRelation,
      pepName: rightData.pep.pepName,
      pepSurname: rightData.pep.pepSurname,
    });
  };

  return {
    onSaveUser,
    loading: saveCustomer.isLoading,
  };
};
